import React from 'react';
import type { IntlShape } from 'react-intl-next';

import type {
	PublicPluginAPI,
	QuickInsertHandlerFn,
	EditorCommand,
} from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import type {
	QuickInsertItem,
	QuickInsertActionInsert,
} from '@atlaskit/editor-common/provider-factory';

import type { Snippet } from '../snippetTypes.ts';
import {
	createSnippetDescription,
	extractSnippetKeywords,
	getSnippetQuickInsertKey,
} from '../snippetUtils.ts';

/**
 * Adds a snippet to the quick insert menu using the editor API.
 */
export const addSnippetQuickInsertItem = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: PublicPluginAPI<[QuickInsertPlugin]> | null | undefined;
}) => {
	if (!editorAPI) {
		return;
	}
	const addQuickInsertItem: ((item: QuickInsertHandlerFn) => EditorCommand) | undefined =
		editorAPI?.quickInsert?.commands?.addQuickInsertItem;

	if (addQuickInsertItem === undefined) {
		return;
	}

	editorAPI.core?.actions.execute(
		addQuickInsertItem((intl: IntlShape) => [createSnippetQuickInsertItem({ snippet, intl })]),
	);
};

/**
 * Updates an existing snippet in the editor's quick insert menu (e.g. after edit or share/unshare).
 * No-op if editorAPI is null/undefined or updateQuickInsertItem is not available.
 */
export const updateSnippetInQuickInsertItem = ({
	snippet,
	editorAPI,
}: {
	snippet: Snippet;
	editorAPI: PublicPluginAPI<[QuickInsertPlugin]> | null | undefined;
}): void => {
	if (!editorAPI) {
		return;
	}
	const updateQuickInsertItem = editorAPI?.quickInsert?.commands?.updateQuickInsertItem ?? null;
	if (!updateQuickInsertItem) {
		return;
	}
	editorAPI.core?.actions.execute(
		updateQuickInsertItem(getSnippetQuickInsertKey(snippet.snippetId), (intl: IntlShape) => [
			createSnippetQuickInsertItem({ snippet, intl }),
		]),
	);
};

/**
 * Creates a snippet quick insert item.
 */
export const createSnippetQuickInsertItem = ({
	snippet,
	intl,
}: {
	snippet: Snippet;
	intl: IntlShape;
}): QuickInsertItem => {
	return {
		key: getSnippetQuickInsertKey(snippet.snippetId),
		title: snippet.name,
		description: createSnippetDescription(snippet.createdBy, intl),
		keywords: extractSnippetKeywords(snippet.name, intl),
		isDisabledOffline: true,
		icon: () => <TemplateIcon label="Snippet" />,
		action: (insert: QuickInsertActionInsert, state: EditorState) => {
			const body = snippet.body;
			if (!body) {
				return false;
			}

			if (Array.isArray(body)) {
				const fragment = Fragment.fromJSON(state.schema, body);
				return insert(fragment);
			}

			return insert(body);
		},
	};
};
